import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Form, FormContext, Modal, ModalContext, toast } from "dash-ui";
import { useTranslation } from "react-i18next";

import { CreateUserFormFields } from "pages/api-management/constants/constants";
import { useUserFormValidation } from "pages/management/hooks/useUserFormValidation";
import {
  GetApiUsersDocument,
  useAddUserToApiMutation,
  useUpdateUserApiMutation,
} from "gql/generated.graphql";

const SaveCompanyModal = () => {
  const { t } = useTranslation(["common", "forms", "pages"]);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { toggleModal, isOpenCurrent, payload, setPayload } =
    useContext(ModalContext);
  const { validate, isError } = useUserFormValidation();
  const [createUser, { loading: loadingCreate }] = useAddUserToApiMutation({
    refetchQueries: [GetApiUsersDocument],
  });
  const [updateUser, { loading: loadingUpdate }] = useUpdateUserApiMutation({
    refetchQueries: [GetApiUsersDocument],
  });
  const {
    stateHandlers,
    formState,
    formErrors,
    formOnSubmit,
    resetForm,
    resetFormErrors,
    setFormValues,
  } = useContext(FormContext);

  const isOpen = isOpenCurrent["AddDashAPIUser"];
  const [isEdit, setIsEdit] = useState(false);
  const [enablePasswordChange, setEnablePasswordChange] = useState(false);

  useEffect(() => {
    const isEditMode = Boolean(payload);
    if (isEditMode) {
      setFormValues({
        firstName: payload?.user?.profile?.firstName || "",
        lastName: payload?.user?.profile?.lastName || "",
        profileId: payload?.user?.profile?._id || "",
        accountId: payload?.user?.account?._id || "",
        email: payload?.user?.account?.email || "",
        excludeFromReports: payload?.user?.account?.excludeFromReports || "",
        password: "",
        referralCode: payload?.user?.apiCredentials?.referralCode,
        companyName: payload?.user?.apiCredentials?.companyName || "",
      });
    } else {
      setFormValues({
        firstName: payload?.firstName || "",
        lastName: payload?.lastName || "",
        email: payload?.email || "",
        password: "",
        referralCode: payload?.user?.apiCredentials?.referralCode,
        companyName: payload?.user?.apiCredentials?.companyName || "",
      });
    }

    setIsEdit(isEditMode);
  }, [payload]);

  const closeModal = () => {
    toggleModal(false, "AddDashAPIUser");
    setPayload(null);
    setEnablePasswordChange(false);
    resetForm();
    resetFormErrors();
  };

  const handleFieldsValues =
    (field: CreateUserFormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      stateHandlers[field](value);
      validate(field, value);
    };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();

    const {
      accountId,
      profileId,
      firstName,
      lastName,
      email,
      excludeFromReports,
      password,
      referralCode,
      companyName,
    } = formState;

    if (isEdit) {
      updateUser({
        variables: {
          accountUpdateInput: {
            _id: accountId,
            // password: password.length ? password : undefined,
            email,
          },
          profileInput: {
            _id: profileId,
            firstName,
            lastName,
          },
          apiCredentialsInput: {
            referralCode,
            companyName,
          },
        },
        onCompleted: () => {
          closeModal();
          toast.success(t("pages:managementPage.toasts.userUpdated"));
        },
        onError: (error: any) => {
          toast.error(error.message);
        },
      });

      return;
    }
    createUser({
      variables: {
        accountInput: {
          password,
          email,
          excludeFromReports,
        },
        profileInput: {
          firstName,
          lastName,
        },
        apiCredentialsInput: {
          referralCode,
        },
      },
      onCompleted: () => {
        closeModal();
        toast.success(t("pages:managementPage.toasts.userAdded"));
      },
      onError: (error: any) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={
        isEdit
          ? t("pages:apiUsersPage.editUser").toString()
          : t("pages:apiUsersPage.addNewUser")?.toString()
      }
      submitButton={{
        title: isEdit
          ? t("common:labels.update").toString()
          : t("common:labels.add").toString(),
        disabled: isError || loadingUpdate || loadingCreate,
        isLoading: loadingUpdate || loadingCreate,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: closeModal,
      }}
    >
      <Form onSubmit={submitForm} ref={formRef}>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.firstName")}</Form.Label>
          <Form.Input
            placeholder={t("forms:placeholders.firstName") || ""}
            value={formState.firstName || ""}
            onChange={handleFieldsValues(CreateUserFormFields.FirstName)}
            $errorType={formErrors[CreateUserFormFields.FirstName]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.FirstName]?.type}
          >
            {formErrors[CreateUserFormFields.FirstName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.lastName")}</Form.Label>
          <Form.Input
            value={formState.lastName || ""}
            placeholder={t("forms:placeholders.lastName") || ""}
            onChange={handleFieldsValues(CreateUserFormFields.LastName)}
            $errorType={formErrors[CreateUserFormFields.LastName]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.LastName]?.type}
          >
            {formErrors[CreateUserFormFields.LastName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.email")}</Form.Label>
          <Form.Input
            value={formState.email || ""}
            placeholder={t("forms:placeholders.email") || ""}
            onChange={handleFieldsValues(CreateUserFormFields.Email)}
            $errorType={formErrors[CreateUserFormFields.Email]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.Email]?.type}
          >
            {formErrors[CreateUserFormFields.Email]?.message}
          </Form.Feedback>
        </Form.Group>
        {/* if needed, we can reactivate this password editing in the future */}
        {/* {isEdit ? (
          <Checkbox
            mt={5}
            label={'Change Password'}
            checked={false}
            onChange={() => setEnablePasswordChange(!enablePasswordChange)}
          />
        ) : (
          <div />
        )} */}

        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.apiReferralCode")}</Form.Label>
          <Form.Input
            value={formState.referralCode || ""}
            placeholder={t("forms:placeholders.apiReferralCode") || ""}
            onChange={handleFieldsValues(CreateUserFormFields.ReferralCode)}
            $errorType={formErrors[CreateUserFormFields.ReferralCode]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.ReferralCode]?.type}
          >
            {formErrors[CreateUserFormFields.ReferralCode]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t("forms:labels.apiCompany")}</Form.Label>
          <Form.Input
            value={formState.companyName || ""}
            placeholder={t("forms:placeholders.apiCompanyName") || ""}
            onChange={handleFieldsValues(CreateUserFormFields.CompanyName)}
            $errorType={formErrors[CreateUserFormFields.CompanyName]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.CompanyName]?.type}
          >
            {formErrors[CreateUserFormFields.CompanyName]?.message}
          </Form.Feedback>
        </Form.Group>
        {!isEdit && (
          <Form.Group w="100%" mt={3}>
            <Form.Label>{t("forms:labels.password")}</Form.Label>
            <Form.InputPassword
              value={formState.password || ""}
              disabled={isEdit && !enablePasswordChange}
              onChange={handleFieldsValues(CreateUserFormFields.Password)}
              placeholder={
                isEdit ? "*******" : t("forms:placeholders.password") || ""
              }
              $errorType={formErrors[CreateUserFormFields.Password]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[CreateUserFormFields.Password]?.type}
            >
              {formErrors[CreateUserFormFields.Password]?.message}
            </Form.Feedback>
          </Form.Group>
        )}
      </Form>
    </Modal>
  );
};

export default SaveCompanyModal;
